import { finalize } from 'rxjs/operators';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { LocalStorageService } from '../../../xform-compat';
import { interval, Subscription } from 'rxjs';
import { NotificationsSettingsService } from '../../../core/data-services';
import { Notification, UnreadNotifications } from '../../../shared/models';
import { AuthenticationService, AuthorizationService } from '../../../core/services';

/**
 * Header notification component that shows notification count
 * and opens a notification menu on click.
 */
@Component({
  selector: 'rms-header-notification',
  templateUrl: './header-notification.component.html',
  styleUrls: ['./header-notification.component.scss'],
  standalone: false
})
export class HeaderNotificationComponent implements OnInit, OnDestroy {
  @ViewChild('menuTrigger') menuTrigger: MatMenuTrigger;

  private timerSubscription: Subscription;
  private unreadTotal$: Subscription;

  public notifications: Array<Notification>;
  public total = 0;

  constructor(
    private notificationsSettingsService: NotificationsSettingsService,
    private authenticationService: AuthenticationService,
    private authorizationService: AuthorizationService,
    private storage: LocalStorageService
  ) {
    this.notifications = this.storage.get('notifications') || [];
    this.unreadTotal$ = this.notificationsSettingsService.getUnreadTotal().subscribe(
      ({ unreadNotifications, reduce }) => {
        if (unreadNotifications !== undefined) {
          this.total = reduce ? this.total - unreadNotifications : unreadNotifications;
        }
      }
    );
  }

  ngOnInit(): void {
    this.getNotifications();
  }

  ngOnDestroy(): void {
    this.unreadTotal$?.unsubscribe();
    this.timerSubscription?.unsubscribe();
  }

  /**
   * Fetches unread notifications from backend
   */
  getNotifications(): void {
    const userId = this.authenticationService.getLoggedUser()?.id;
    if (!userId) {
      return;
    }

    let scopes = ['COMPANY'];
    if (this.authorizationService.hasPermission(['SONAR_READ'])) {
      scopes = [...scopes, 'USER'];
    }

    this.notificationsSettingsService.getUnreadNotifications(userId, scopes)
      .pipe(finalize(() => this.startTimer()))
      .subscribe((value: UnreadNotifications) => {
        this.notificationsSettingsService.updateUnreadTotal(value.unreadNotifications);
        this.notificationsSettingsService.updateUnreadByType(value.unreadNotificationCounts);
      });
  }

  /**
   * Closes the notification menu
   */
  public closeMenu(): void {
    this.menuTrigger?.closeMenu();
  }

  /**
   * Starts timer to refresh notifications every 60 seconds
   */
  private startTimer(): void {
    this.timerSubscription?.unsubscribe();
    this.timerSubscription = interval(60000).subscribe(() => {
      this.getNotifications();
    });
  }
}
